import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Per-user pinned workflow tools, scoped to the current device via
 * localStorage. We don't sync to the server yet — when we do, this hook is the
 * single integration point.
 *
 * Limit: 5 pinned tools per user.
 */
const MAX_FAVORITES = 5;

const storageKey = (userId: string) => `riskninja:workflow-favorites:${userId}`;

const readFavorites = (userId: string): string[] => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const writeFavorites = (userId: string, favorites: string[]) => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(favorites));
  } catch {
    // Storage full or disabled — silent failure is fine, user just won't see
    // their pins persist.
  }
};

export const useWorkflowFavorites = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    if (!userId) {
      setFavorites([]);
      return;
    }
    setFavorites(readFavorites(userId));
  }, [userId]);

  const isFavorite = useCallback(
    (workflowType: string) => favorites.includes(workflowType),
    [favorites]
  );

  const toggleFavorite = useCallback(
    (workflowType: string) => {
      if (!userId) return;
      setFavorites((prev) => {
        const next = prev.includes(workflowType)
          ? prev.filter((w) => w !== workflowType)
          : prev.length >= MAX_FAVORITES
          ? prev // at cap, ignore further pins
          : [...prev, workflowType];
        writeFavorites(userId, next);
        return next;
      });
    },
    [userId]
  );

  return {
    favorites,
    isFavorite,
    toggleFavorite,
    canAddMore: favorites.length < MAX_FAVORITES,
    max: MAX_FAVORITES,
  };
};
